import { ComponentProps, useState } from 'react';
import Select from 'react-select';
import { Option } from 'types/types';

export type CustomOptionsFilter = (allOptions: Option[], inputText: string) => Option[];

type Props = ComponentProps<typeof Select> & {
  customOptionsFilter: CustomOptionsFilter;
};

/**
 * Supports a custom function for options text filtering (and possibly sorting).
 * For simpler cases consider using {@link Props.filterOption}.
 * Inspired by https://stackblitz.com/edit/react-select-match-sorter?file=index.js
 */
const SelectWithCustomOptionsFilter: React.FC<Props> = ({ options, customOptionsFilter, ...otherProps }) => {
  const [filteredOptions, setFilteredOptions] = useState(() =>
    customOptionsFilter((options as unknown as Option[]) || [], '')
  );

  return (
    <Select
      {...otherProps}
      options={filteredOptions}
      onInputChange={(newText) => {
        const allOptions = (options as unknown as Option[]) || [];
        const filteredOptions = customOptionsFilter(allOptions, newText);
        setFilteredOptions(filteredOptions);
      }}
    />
  );
};
export default SelectWithCustomOptionsFilter;

export const exactMatchesFirstCustomOptionsFilter: CustomOptionsFilter = (allOptions, inputText) => {
  if (inputText.length === 0) {
    return allOptions;
  }

  const exactMatches: Option[] = [];
  const partialMatches: Option[] = [];

  const normalizedInput = inputText.trim().toLowerCase();

  for (const option of allOptions) {
    const normalizedOption = option.label.trim().toLowerCase();
    if (normalizedOption.startsWith(normalizedInput)) {
      exactMatches.push(option);
    } else if (normalizedOption.includes(normalizedInput)) {
      partialMatches.push(option);
    }
  }

  return [...exactMatches, ...partialMatches];
};
